import React, { useEffect, useState } from 'react'

import {
  Box,
  Button,
  Center,
  Flex,
  HStack,
  Heading,
  Spinner,
  VStack,
} from '@chakra-ui/react'
import {
  AgoraRTCReactError,
  ICameraVideoTrack,
  IMicrophoneAudioTrack,
} from 'agora-rtc-react'
import { Close, Reload } from 'src/components/icon'
import {
  StaticScreen,
  StaticScreenTitle,
  StaticScreenContent,
} from 'src/components/StaticScreen/StaticScreen'
import { useAuthenticatedAccount } from 'src/features/auth/context'

import { CameraPreviewPanel } from './CameraPreviewPanel'
import { ConnectionTestResultSummary } from './ConnectionTestResultSummary'
import { MicrophoneLevelPanel } from './MicrophoneLevelPanel'
import { NetworkTestPanel } from './NetworkTestPanel'
import { useLocalPlayer } from '../../context/localPlayerContext'
import { useConnectionTestVideoCallToken } from '../../hooks/useConnectionTestVideoCallToken'
import {
  NetworkTestResult,
  NetworkTestState,
} from '../../hooks/useTwoClientNetworkTest'
import { canUseVideoCallConnectionTest } from '../../utils/canUseVideoCallConnectionTest'
import { VideoCallRoomProvider } from '../VideoCallRoomProvider/VideoCallRoomProvider'

type LocalPlayerStatus = {
  localCameraTrack: ICameraVideoTrack | null
  localCameraError: AgoraRTCReactError | null
  localMicrophoneTrack: IMicrophoneAudioTrack | null
  localMicrophoneError: AgoraRTCReactError | null
}

/**
 * VideoCallRoomProvider の内側で localPlayer の状態を親に伝えるブリッジ。
 * Providerの中で localPlayer を生成するため、Providerの外で localPlayer の状態を参照するにはこのような形が必要。
 */
const LocalPlayerBridge: React.FC<{
  onStatus: (status: LocalPlayerStatus) => void
}> = ({ onStatus }) => {
  const {
    localCameraTrack,
    localCameraError,
    localMicrophoneTrack,
    localMicrophoneError,
  } = useLocalPlayer()

  useEffect(() => {
    onStatus({
      localCameraTrack,
      localCameraError,
      localMicrophoneTrack,
      localMicrophoneError,
    })
  }, [
    localCameraTrack,
    localCameraError,
    localMicrophoneTrack,
    localMicrophoneError,
    onStatus,
  ])

  return null
}

export const VideoCallConnectionTestScreen: React.FC = () => {
  const { account } = useAuthenticatedAccount()

  const canUseVideoCallTest = canUseVideoCallConnectionTest(account.member.role)

  const tokenData = useConnectionTestVideoCallToken({ canUseVideoCallTest })

  const [networkTestState, setNetworkTestState] =
    useState<NetworkTestState>('idle')
  const [networkResult, setNetworkResult] = useState<NetworkTestResult | null>(
    null,
  )
  const [localPlayerStatus, setLocalPlayerStatus] = useState<LocalPlayerStatus>(
    {
      localCameraTrack: null,
      localCameraError: null,
      localMicrophoneTrack: null,
      localMicrophoneError: null,
    },
  )

  if (!canUseVideoCallTest) {
    return (
      <StaticScreen>
        <StaticScreenTitle>ビデオ接続テストを利用できません</StaticScreenTitle>
        <StaticScreenContent>
          ブラウザのタブを閉じてください。
        </StaticScreenContent>
      </StaticScreen>
    )
  }
  if (!tokenData)
    return (
      <Center h="100svh">
        <Spinner size="lg" />
      </Center>
    )

  return (
    <Box h="100svh" overflow="auto">
      <Box maxW="900px" mx="auto" px="6" py="8">
        <Flex justify="space-between" align="center" mb="6">
          <Heading size="md">ビデオ通話接続確認</Heading>
          <HStack spacing="2">
            <Button
              variant="outline"
              leftIcon={<Reload />}
              onClick={() => window.location.reload()}
            >
              再読み込み
            </Button>
            <Button
              variant="outline"
              leftIcon={<Close />}
              onClick={() => window.close()}
            >
              閉じる
            </Button>
          </HStack>
        </Flex>

        <VStack spacing="6" align="stretch">
          {/*
           * VideoCallRoomProvider はカメラ・マイクの管理にのみ使用する。
           * ネットワークテストは NetworkTestPanel が独立した2クライアントで行うため、
           * Provider の join() は呼び出さない。
           * LocalPlayerBridge で localPlayer の状態を Provider 外へ伝える。
           */}
          <VideoCallRoomProvider
            roomUid={tokenData.videoCallRoomUid}
            token={null}
            videoCallUserUid={null}
            screenShareUserUid={null}
            screenShareToken={null}
            scene="ConnectionTest"
            agoraCloudProxyMode={tokenData.agoraCloudProxyMode}
            isConnectionTest={true}
            enableJoinSound={false}
          >
            <LocalPlayerBridge onStatus={setLocalPlayerStatus} />
            <CameraPreviewPanel />
            <MicrophoneLevelPanel />
          </VideoCallRoomProvider>

          <NetworkTestPanel
            roomUid={tokenData.videoCallRoomUid}
            outboundToken={tokenData.outboundToken}
            outboundUid={tokenData.outboundVideoCallUserUid}
            inboundToken={tokenData.inboundToken}
            inboundUid={tokenData.inboundVideoCallUserUid}
            agoraCloudProxyMode={tokenData.agoraCloudProxyMode}
            onStateChange={setNetworkTestState}
            onResult={setNetworkResult}
          />

          <ConnectionTestResultSummary
            localCameraTrack={localPlayerStatus.localCameraTrack}
            localCameraError={localPlayerStatus.localCameraError}
            localMicrophoneTrack={localPlayerStatus.localMicrophoneTrack}
            localMicrophoneError={localPlayerStatus.localMicrophoneError}
            networkTestState={networkTestState}
            networkResult={networkResult}
          />
        </VStack>
      </Box>
    </Box>
  )
}
